import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Autocomplete, Box, Button, Card, CardContent, Checkbox, Container, FormControlLabel,
    IconButton, InputAdornment, Link, TextField, Toolbar, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_NAME, ROUTES } from '@/config/app.config';
import { COUNTRIES, type Country } from '@/config/countries';
import { ApiError } from '@/infrastructure/services/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

interface FormData {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
}

const initialFormData: FormData = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
};

export function RegisterPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { register } = useAuthStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [form, setForm] = useState<FormData>(initialFormData);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const updateForm = (field: keyof FormData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const isFormValid = form.name && form.email && form.password && form.password === form.confirmPassword && form.password.length >= 8 && selectedCountry && acceptTerms;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid) {
            return;
        }

        setLoading(true);
        try {
            await register(form.name, form.email, form.password, {
                country: selectedCountry?.name,
                acceptTerms: true,
            });
            addNotification(t('notifications.welcomeEmail'), 'success');
            navigate(ROUTES.dashboard);
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            } else {
                addNotification(t('errors.emailExists'), 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                background: mode === 'dark'
                    ? 'linear-gradient(180deg, #11132B 0%, #1A1C3E 100%)'
                    : 'linear-gradient(180deg, #FAFBFC 0%, #F0F2F5 100%)',
            }}
        >
            <Container maxWidth="sm">
                <Toolbar />
                <Card
                    sx={{
                        my: 4,
                        border: '1px solid',
                        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontWeight: 800,
                                    color: 'primary.main',
                                    mb: 1,
                                }}
                            >
                                {APP_NAME}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {t('auth.register.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('auth.register.subtitle')}
                            </Typography>
                        </Box>

                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Required fields */}
                            <TextField
                                fullWidth label={t('auth.register.name')} value={form.name}
                                onChange={(e) => updateForm('name', e.target.value)} required
                            />
                            <TextField
                                fullWidth label={t('auth.register.email')} type="email" value={form.email}
                                onChange={(e) => updateForm('email', e.target.value)} required
                            />
                            <TextField
                                fullWidth label={t('auth.register.password')} value={form.password}
                                type={showPassword ? 'text' : 'password'}
                                onChange={(e) => updateForm('password', e.target.value)} required
                                helperText={form.password && form.password.length < 8 ? t('errors.weakPassword') : ''}
                                error={!!form.password && form.password.length < 8}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <TextField
                                fullWidth label={t('auth.register.confirmPassword')}
                                type="password" value={form.confirmPassword}
                                onChange={(e) => updateForm('confirmPassword', e.target.value)} required
                                error={!!form.confirmPassword && form.password !== form.confirmPassword}
                                helperText={form.confirmPassword && form.password !== form.confirmPassword ? t('errors.passwordMismatch') : ''}
                            />
                            <Autocomplete
                                options={COUNTRIES}
                                getOptionLabel={(option) => option.name}
                                value={selectedCountry}
                                onChange={(_e, value) => setSelectedCountry(value)}
                                filterOptions={(options, { inputValue }) => {
                                    const q = inputValue.toLowerCase();
                                    return options.filter(
                                        (o) => o.name.toLowerCase().includes(q) || o.nameEn.toLowerCase().includes(q),
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('auth.register.country')} required />
                                )}
                                fullWidth
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={acceptTerms}
                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        {t('auth.register.acceptTerms')}{' '}
                                        <Link href="#" sx={{ fontWeight: 600 }}>
                                            {t('auth.register.termsLink')}
                                        </Link>
                                    </Typography>
                                }
                                sx={{ mt: 1 }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading || !isFormValid}
                                sx={{ py: 1.5, fontWeight: 700, borderRadius: 3, mt: 1 }}
                            >
                                {loading ? t('common.loading') : t('auth.register.submit')}
                            </Button>
                        </Box>

                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('auth.register.hasAccount')}{' '}
                                <Link
                                    component="button"
                                    onClick={() => navigate(ROUTES.login)}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('common.signIn')}
                                </Link>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

export default RegisterPage;
